import { CHARGING_RENDERERS, chargingParameter } from '@/runtime/charging-statistics-model'
import baseList from '@/option/base'
import { cloneNodes } from './node-operations'
import { createPageId } from '@/schema/page-project'
import { navigationIssue } from '@/runtime/page-navigation'
import { templatePageConfigKeys, validateTemplatePageConfig } from '@/schema/page-config'
import { validateEchartsOption } from './echarts-option'
import { deviceStatusQuery } from '@/runtime/device-status-model'
import { deviceStatusDemoStatistics } from '@/components/DeviceStatus/presentation'
import { demoControlProperty, demoControlObserved } from '@/components/DeviceProperty/demo'
import { weeklyPlan } from '@/components/WeekSchedule/model'
import { COMPONENT_TITLE_OPTION_KEYS, componentTitleSettings } from '@/components/ComponentTitle/presentation'

export const TEMPLATE_DRAG_TYPE = 'application/x-ironman-component-template'
export const TEMPLATE_RENDERERS = ['bar', 'line', 'pie', 'radar', 'scatter', 'funnel', 'gauge', 'progress', 'flop', 'wordcloud', 'pictorialbar', 'text', 'datetime', 'img', 'imgborder', 'Rectangle', 'Round', 'StraightLine', 'VerticalLine', 'tabs', 'navigation', 'productList', 'deviceList', 'deviceStatus', 'componentTitle']
// 整页／整项目复用配置，不受纯组件样式模板的类型限制。
export const PAGE_TEMPLATE_RENDERERS = [...TEMPLATE_RENDERERS, 'common', 'map', 'Dmap', 'swiper', 'iframe', 'video', 'Player', 'table', 'test', 'deviceProperty', 'deviceGroup', 'weekSchedule']
const rootKeys = ['index', 'name', 'title', 'left', 'top', 'component', 'option', 'data', 'dataType', 'dataMethod', 'dataQuery', 'dataFormatter', 'dataScript', 'url', 'time', 'rotate', 'opacity', 'display', 'locked', 'children', 'menu', 'child', 'imageResource', 'parameter']
const pageScriptKeys = ['dataScript', 'dataFormatter', 'echartFormatter', 'labelFormatter', 'clickFormatter', 'titleFormatter', 'formatter', 'markerConfig', 'infoWindowConfig']
const pageRootKeys = [...rootKeys, ...pageScriptKeys.filter(key => !rootKeys.includes(key)), 'dataAppend']
const forbidden = /token|password|previewProduct|previewDevice|pageId|targetPage|authorization|__proto__|constructor|prototype/i
const componentForbidden = /formatter|script|token|password|deviceId|deviceName|productId|productName|previewProduct|pageId|targetPage|authorization|__proto__|constructor|prototype/i
const dragging = new Map()
const copy = value => JSON.parse(JSON.stringify(value))
const object = value => value && typeof value === 'object' && !Array.isArray(value)
const deviceRenderers = ['text', 'gauge', 'line', 'bar', 'weekSchedule']
const demoKeys = ['demoProperty', 'demoValue', 'demoValuesByDevice', 'demoStatistics', 'demoStatisticsByProduct']
const sampleReaders = ['weekSchedule']
const bindingTextKeys = ['deviceSource', 'deviceId', 'deviceName', 'productId', 'productName', 'property', 'propertyName', 'propertyType', 'propertyUnit', 'rangeMode', 'aggregationType', 'interval', 'time', 'format', 'formatterInput']
const bindingKeys = [...bindingTextKeys, 'configDataType', 'limit', 'from', 'to', 'liveFollow', 'refreshInterval']

function validateBinding (parameter) {
  if (!object(parameter) || Object.keys(parameter).some(key => !bindingKeys.includes(key))) throw new Error('模板设备属性配置包含不支持的字段')
  Object.entries(parameter).forEach(([key, value]) => {
    if (bindingTextKeys.includes(key) && (typeof value !== 'string' || value.length > 512)) throw new Error('模板设备属性配置格式不正确')
    if (['from', 'to'].includes(key) && !(typeof value === 'string' && value.length <= 128) && !Number.isFinite(value)) throw new Error('模板查询时间格式不正确')
    if (['configDataType', 'limit', 'refreshInterval'].includes(key) && (!Number.isSafeInteger(value) || value < 0)) throw new Error('模板查询参数格式不正确')
    if (key === 'liveFollow' && typeof value !== 'boolean') throw new Error('模板实时接续配置不正确')
    clean(value)
  })
  if (!['fixed', 'page'].includes(parameter.deviceSource) || (parameter.configDataType !== undefined && ![0, 1, 2].includes(parameter.configDataType)) || parameter.limit > 10000) throw new Error('模板设备来源或查询类型不正确')
  if (parameter.formatterInput !== undefined && !['legacy', 'raw'].includes(parameter.formatterInput)) throw new Error('模板处理函数输入不正确')
  if (parameter.deviceSource === 'page' && (parameter.deviceId || parameter.deviceName)) throw new Error('页面设备绑定不能携带固定或示例设备')
}

function createBinding (source = {}) {
  const parameter = { deviceSource: source.deviceSource === 'page' ? 'page' : 'fixed' }
  bindingKeys.forEach(key => {
    if (source[key] !== undefined) parameter[key] = ['configDataType', 'limit', 'refreshInterval'].includes(key) ? Number(source[key]) : copy(source[key])
  })
  if (parameter.deviceSource === 'page') { delete parameter.deviceId; delete parameter.deviceName }
  validateBinding(parameter)
  return parameter
}

function validateDemoConfiguration (name, option) {
  if (!['deviceStatus', 'deviceProperty'].includes(name)) return
  if (option.dataSource !== undefined && !['static', 'platform'].includes(option.dataSource)) throw new Error('模板演示数据来源无效')
  if (option.dataSource !== 'static') {
    if (demoKeys.some(key => option[key] !== undefined)) throw new Error('平台模板不能携带演示值或演示能力')
    return
  }
  const mapped = (value, validate) => {
    if (value === undefined) return
    if (!object(value) || Object.keys(value).length > 1000) throw new Error('模板演示映射格式无效')
    Object.entries(value).forEach(([key, item]) => { if (!/^[A-Za-z0-9_-]{1,64}$/.test(key)) throw new Error('模板演示编号无效'); validate(item) })
  }
  if (name === 'deviceStatus') {
    deviceStatusDemoStatistics(option.demoStatistics || {})
    mapped(option.demoStatisticsByProduct, deviceStatusDemoStatistics)
  } else if (name === 'deviceProperty') {
    const property = demoControlProperty(option)
    demoControlObserved(property, option.demoValue)
    mapped(option.demoValuesByDevice, value => demoControlObserved(property, value))
  }
}
function stripPlatformDemos (option) {
  if (option.dataSource !== 'static') demoKeys.forEach(key => { delete option[key] })
}

function validatePageConfig (snapshot) {
  validateTemplatePageConfig(snapshot.config, snapshot.width, snapshot.height)
}

// 组件模板不携带接口、身份、脚本或跨项目导航；页面模板按整页复用保留组件数据源配置。
function clean (value, depth = 0) {
  if (depth > 32) throw new Error('模板配置嵌套过深')
  if (Array.isArray(value)) return value.map(item => clean(item, depth + 1))
  if (!object(value)) {
    if (typeof value === 'string' && /<\s*(script|iframe|object|embed)|on[a-z]+\s*=|javascript\s*:|data\s*:text[/]html/i.test(value)) throw new Error('模板不能包含可执行内容')
    return value
  }
  const result = {}
  Object.keys(value).forEach(key => {
    if (!componentForbidden.test(key) && !['url', 'href', 'link', 'dataQuery', 'parameter', 'navigation', 'markerConfig', 'infoWindowConfig'].includes(key)) result[key] = clean(value[key], depth + 1)
  })
  return result
}
function cleanComponentValue (value) {
  if (!object(value)) return clean(value)
  const result = {}
  Object.keys(value).forEach(key => {
    if (!['dataMethod', 'dataQuery', 'dataFormatter', 'dataScript', 'url', 'parameter'].includes(key)) result[key] = clean(value[key])
  })
  return result
}
function cleanPageValue (value, key = '') {
  const sanitize = (entry, depth = 0) => {
    if (depth > 32) throw new Error('模板配置嵌套过深')
    if (Array.isArray(entry)) return entry.map(item => sanitize(item, depth + 1))
    if (!object(entry)) return entry
    const result = {}
    Object.keys(entry).forEach(name => { if (!forbidden.test(name) && !['navigation', 'href', 'link'].includes(name)) result[name] = sanitize(entry[name], depth + 1) })
    return result
  }
  const result = sanitize(value)
  pageValueGuard(result, 0, key)
  return copy(result)
}
function pageValueGuard (value, depth = 0, key = '') {
  if (depth > 32) throw new Error('模板配置嵌套过深')
  if (Array.isArray(value)) return value.forEach(item => pageValueGuard(item, depth + 1, key))
  if (object(value)) {
    Object.keys(value).forEach(name => {
      if (forbidden.test(name)) throw new Error('页面模板包含未支持的身份或项目绑定')
      pageValueGuard(value[name], depth + 1, name)
    })
  } else if (typeof value === 'string' && !pageScriptKeys.includes(key) && /<\s*(script|iframe|object|embed)|on[a-z]+\s*=|javascript\s*:|data\s*:text[/]html/i.test(value)) throw new Error('模板不能包含可执行内容')
}

export function templateLeaves (nodes) {
  return nodes.flatMap(node => node.children ? templateLeaves(node.children) : [node])
}

export function validateTemplateSnapshot (snapshot) {
  if (object(snapshot) && snapshot.kind === 'project') return validateProjectSnapshot(snapshot)
  if (!object(snapshot) || snapshot.schemaVersion !== 1 || !['component', 'page'].includes(snapshot.kind) ||
      !Array.isArray(snapshot.nodes) || (snapshot.kind === 'component' && !snapshot.nodes.length) || Object.keys(snapshot).some(key => !['schemaVersion', 'kind', 'width', 'height', 'nodes', ...(snapshot.kind === 'page' ? ['config'] : [])].includes(key))) throw new Error('模板格式不正确')
  for (const key of ['width', 'height']) if (!Number.isFinite(snapshot[key]) || snapshot[key] <= 0 || snapshot[key] > 20000) throw new Error('模板尺寸须在 0～20000 像素内')
  if (snapshot.kind === 'page') validatePageConfig(snapshot)
  if (JSON.stringify(snapshot).length > 512000) throw new Error('模板配置超过 500 KB')
  const ids = new Set(), references = []
  let count = 0
  const visit = (nodes, depth) => {
    if (depth > 12 || (!nodes.length && (depth > 0 || snapshot.kind === 'component'))) throw new Error('模板分组为空或层级过深')
    nodes.forEach(node => {
      if (!object(node) || ++count > 200 || !/^[A-Za-z0-9_-]{1,64}$/.test(node.index) || ids.has(node.index) || Object.keys(node).some(key => !(snapshot.kind === 'page' ? pageRootKeys : rootKeys).includes(key))) throw new Error('模板图层无效或数量超过 200')
      ids.add(node.index)
      if (node.children) {
        if (!Array.isArray(node.children) || node.component || node.parameter) throw new Error('模板分组格式不正确')
        visit(node.children, depth + 1)
      } else {
        const c = node.component
        if (!object(c) || !(snapshot.kind === 'page' ? PAGE_TEMPLATE_RENDERERS : TEMPLATE_RENDERERS).includes(c.name) || c.name !== c.prop || Object.keys(c).some(key => !['name', 'prop', 'width', 'height'].includes(key))) throw new Error('此组件尚不支持保存为配置模板')
        if (node.option && node.option.echartsOption !== undefined) validateEchartsOption(node.option.echartsOption, c.name)
        if (snapshot.kind === 'page') {
          if (!Number.isSafeInteger(node.dataType) || node.dataType < 0 || (node.dataType > (['map', 'Dmap'].includes(c.name) ? 5 : 4) && !(node.dataType === 6 && CHARGING_RENDERERS.includes(c.name)))) throw new Error('页面模板数据类型不正确')
          if (node.dataType === 6) chargingParameter(node.parameter, c.name)
          if (!Number.isSafeInteger(node.time) || node.time < 0) throw new Error('模板刷新间隔不正确')
          if (node.parameter !== undefined && Number(node.dataType) === 2 && deviceRenderers.includes(c.name)) validateBinding(node.parameter)
          if (object(node.parameter) && node.parameter.deviceSource !== undefined && (Number(node.dataType) !== 2 || !deviceRenderers.includes(c.name))) throw new Error('设备属性绑定与组件数据类型不一致')
        } else if (node.dataType !== 0 || node.time !== 0 || node.parameter !== undefined || node.dataMethod !== undefined || node.dataQuery !== undefined || node.dataFormatter !== undefined || node.dataScript !== undefined || node.url !== undefined) throw new Error('此组件尚不支持保存为数据模板')
        for (const key of ['width', 'height']) if (!Number.isFinite(c[key]) || c[key] <= 0 || c[key] > 20000) throw new Error('组件尺寸无效')
        if (![node.left, node.top].every(value => Number.isFinite(value) && Math.abs(value) <= 20000)) throw new Error('组件坐标无效')
        if (snapshot.kind === 'component' && (node.left < 0 || node.top < 0 || node.left + c.width > snapshot.width + 0.01 || node.top + c.height > snapshot.height + 0.01)) throw new Error('组件超出模板边界')
        if (snapshot.kind === 'page') validateDemoConfiguration(c.name, node.option || {})
        if (snapshot.kind === 'page' && sampleReaders.includes(c.name)) {
          if (![0, 2].includes(node.dataType)) throw new Error('周计划模板只支持静态或设备来源')
          weeklyPlan(node.data)
          if (node.parameter && ['deviceId', 'deviceName', 'productId', 'productName'].some(key => node.parameter[key])) throw new Error('周计划模板不能携带固定设备或产品编号')
        }
        if (c.name === 'componentTitle') {
          const option = node.option, normalized = componentTitleSettings(option || {})
          if (node.dataType !== 0 || node.time !== 0 || !object(node.data) || Object.keys(node.data).length ||
              ['parameter', 'url', 'dataMethod', 'dataQuery', 'dataFormatter', 'dataScript', 'navigation', 'child'].some(key => node[key] !== undefined)) throw new Error('组件标题只能保存静态文字与样式，不能绑定数据或交互')
          if (!object(option) || Object.entries(option).some(([key, value]) => !COMPONENT_TITLE_OPTION_KEYS.includes(key) || normalized[key] !== value)) throw new Error('组件标题样式格式无效')
        }
        if (c.name === 'deviceStatus') {
          const option = node.option || {}
          if (node.dataType !== 0 || node.time !== 0 || node.parameter !== undefined || !object(node.data) || Object.keys(node.data).length || ['url', 'dataMethod', 'dataQuery', 'dataFormatter', 'dataScript'].some(key => node[key] !== undefined)) throw new Error('设备统计模板不能包含接口、脚本或运行值')
          deviceStatusQuery(option, option.productScope === 'page' ? { productId: 'template-context' } : {}, false)
          if (snapshot.kind === 'component' && (option.productScope !== 'all' || option.productId || option.productIds || option.previewProductId)) throw new Error('设备统计样式不能包含真实产品绑定')
        }
        if (['productList', 'deviceList'].includes(c.name)) {
          const option = node.option || {}
          if (snapshot.kind === 'page') {
            if (!['static', 'platform'].includes(option.dataSource) || !['all', 'specified', ...(c.name === 'deviceList' ? ['current'] : [])].includes(option.productScope) || !Array.isArray(node.data) || (option.dataSource === 'platform' && node.data.length)) throw new Error('页面列表模板数据来源或范围无效')
            if (option.templateListSource !== undefined && option.templateListSource !== option.dataSource) throw new Error('页面列表模板数据来源不一致')
          } else if (option.dataSource !== 'static' || option.productScope !== 'all' || !Array.isArray(node.data) || node.data.length) throw new Error('列表模板不能包含真实产品或设备数据')
        }
      }
      if (node.child) {
        if (!object(node.child) || !Array.isArray(node.child.index) || Object.keys(node.child).some(key => !['index', 'paramName'].includes(key))) throw new Error('模板内部引用无效')
        references.push(...node.child.index)
      }
    })
  }
  visit(snapshot.nodes, 0)
  if (references.some(id => !ids.has(id))) throw new Error('模板包含外部组件引用')
  if (snapshot.kind === 'page') pageValueGuard(snapshot)
  if (snapshot.kind === 'component' && JSON.stringify(clean(snapshot)) !== JSON.stringify(snapshot)) throw new Error('模板包含未支持的脚本或数据绑定')
  return copy(snapshot)
}

function createSnapshot (nodes, pageConfig) {
  const isPage = !!pageConfig
  const prepare = list => list.map(source => {
    if (source.children) return { index: source.index, name: source.name || '分组', title: source.title || '分组', menu: true, ...(isPage ? { locked: !!source.locked, display: !!source.display } : {}), children: prepare(source.children) }
    const c = source.component
    if (!c || !(isPage ? PAGE_TEMPLATE_RENDERERS : TEMPLATE_RENDERERS).includes(c.name) || c.name !== c.prop) throw new Error(isPage ? '项目中包含未注册的组件类型，无法保存模板' : '所选组件暂不支持纯样式模板，请使用页面模板或项目模板保存完整配置')
    const base = baseList.flatMap(group => group.children).find(item => item.option.component.name === c.name)
    const node = {}
    ;(isPage ? pageRootKeys : rootKeys).forEach(key => { if (source[key] !== undefined && !['child', 'children', 'component', 'data', 'parameter'].includes(key)) node[key] = isPage ? cleanPageValue(source[key], key) : cleanComponentValue({ [key]: source[key] })[key] })
    node.component = { name: c.name, prop: c.prop, width: Number(c.width), height: Number(c.height) }
    const deviceBinding = isPage && Number(source.dataType) === 2 && deviceRenderers.includes(c.name) && source.parameter
    // 设备运行值不是配置，模板保存物模型配置和静态占位值。
    node.data = isPage && (!deviceBinding || sampleReaders.includes(c.name)) ? cleanPageValue(source.data === undefined ? {} : source.data) : clean(Number(source.dataType || 0) === 0 ? (source.data === undefined ? {} : source.data) : (base ? base.option.data : {}))
    if (isPage && ['map', 'Dmap'].includes(c.name) && Number(source.dataType) === 5) node.data = []
    if (deviceBinding && object(source.data) && object(node.data)) ['min', 'max', 'label', 'unit'].forEach(key => { if (source.data[key] !== undefined) node.data[key] = clean(source.data[key]) })
    node.option = isPage ? cleanPageValue(source.option || {}) : clean(source.option || {})
    node.dataType = isPage ? Number(source.dataType || 0) : 0; node.time = isPage ? Number(source.time || 0) : 0; node.locked = isPage ? !!source.locked : false
    if (isPage && source.parameter !== undefined && (Number(node.dataType) === 2 || !deviceRenderers.includes(c.name))) node.parameter = Number(node.dataType) === 2 && deviceRenderers.includes(c.name) ? createBinding(source.parameter) : cleanPageValue(source.parameter)
    if (isPage && node.dataType === 6) {
      node.parameter = chargingParameter(source.parameter, c.name)
      delete node.parameter.previewProductId
      if (node.parameter.productSource === 'page') delete node.parameter.productId
    }
    if (isPage && sampleReaders.includes(c.name) && node.parameter) ['deviceId', 'deviceName', 'productId', 'productName'].forEach(key => { delete node.parameter[key] })
    if (isPage && ['deviceStatus', 'deviceProperty'].includes(c.name)) stripPlatformDemos(node.option)
    node.left = Number(source.left || 0); node.top = Number(source.top || 0)
    if (source.child && Array.isArray(source.child.index)) node.child = { index: source.child.index.slice(), paramName: source.child.paramName || '' }
    if (c.name === 'componentTitle') {
      node.option = Object.fromEntries(Object.entries(node.option).filter(([key]) => COMPONENT_TITLE_OPTION_KEYS.includes(key)))
      node.data = {}; node.dataType = 0; node.time = 0
      for (const key of ['parameter', 'url', 'dataMethod', 'dataQuery', 'dataFormatter', 'dataScript', 'navigation', 'child']) delete node[key]
    }
    if (c.name === 'deviceStatus') {
      node.data = {}; node.dataType = 0; node.time = 0
      for (const key of ['parameter', 'url', 'dataMethod', 'dataQuery', 'dataFormatter', 'dataScript']) delete node[key]
      delete node.option.previewProductId
      if (!isPage) { node.option.productScope = 'all'; delete node.option.productId; delete node.option.productIds }
      else if (node.option.productScope !== 'specified') { delete node.option.productId; delete node.option.productIds }
    }
    if (['productList', 'deviceList'].includes(c.name)) {
      if (isPage) {
        node.option.dataSource = source.option && source.option.dataSource === 'static' ? 'static' : 'platform'
        node.option.templateListSource = node.option.dataSource
        node.option.productScope = (source.option && source.option.productScope) || (c.name === 'deviceList' ? 'specified' : 'all')
        if (node.option.dataSource === 'platform') node.data = []
      } else { node.data = []; node.option.dataSource = 'static'; node.option.productScope = 'all' }
    }
    return node
  })
  const prepared = prepare(nodes), leaves = templateLeaves(prepared)
  if (!isPage && !leaves.length) throw new Error('请先选择组件')
  const x = isPage ? 0 : Math.min(...leaves.map(node => node.left)), y = isPage ? 0 : Math.min(...leaves.map(node => node.top))
  const width = isPage ? Number(pageConfig.width) : Math.max(...leaves.map(node => node.left + node.component.width)) - x
  const height = isPage ? Number(pageConfig.height) : Math.max(...leaves.map(node => node.top + node.component.height)) - y
  // 使用模板内部 ID，清除对未选组件的引用。
  const allIds = new Set()
  const walk = (list, fn) => list.forEach(node => { fn(node); if (node.children) walk(node.children, fn) })
  walk(prepared, node => {
    if (typeof node.index !== 'string' || !node.index || allIds.has(node.index)) throw new Error('所选组件ID无效或重复')
    allIds.add(node.index)
  })
  walk(prepared, node => { if (node.child) node.child.index = node.child.index.filter(id => allIds.has(id)) })
  let next = 0
  const normalized = cloneNodes(prepared, () => 'tpl_' + (++next), { x: -x, y: -y }, { preserveLocks: isPage })
  const snapshot = { schemaVersion: 1, kind: isPage ? 'page' : 'component', width, height, nodes: normalized }
  if (isPage) {
    snapshot.config = { width, height }
    templatePageConfigKeys.slice(2).forEach(key => { if (pageConfig[key] !== undefined) snapshot.config[key] = cleanPageValue(pageConfig[key], key) })
  }
  return validateTemplateSnapshot(snapshot)
}
export function createTemplateSnapshot (nodes) { return createSnapshot(nodes) }
export function createPageTemplateSnapshot (config, nodes) {
  if (!object(config)) throw new Error('请先打开需要保存的页面')
  return createSnapshot(nodes, config)
}

function navigationGuard (action, pages) {
  if (!object(action) || !['none', 'back', 'page', 'product', 'pages'].includes(action.type)) throw new Error('项目模板页面交互格式不正确')
  const keys = (value, allowed) => { if (!object(value) || Object.keys(value).some(key => !allowed.includes(key))) throw new Error('项目模板页面交互包含不支持的字段') }
  keys(action, action.type === 'page' ? ['type', 'pageId', 'params'] : action.type === 'product' ? ['type', 'routes', 'fallback'] : action.type === 'pages' ? ['type', 'source', 'items', 'inheritContext'] : ['type'])
  const issue = navigationIssue(action, pages)
  if (issue) throw new Error(issue)
  if (action.type === 'page' && action.params !== undefined) {
    keys(action.params, ['productId', 'deviceId'])
    Object.values(action.params).forEach(rule => {
      keys(rule, rule.source === 'literal' ? ['source', 'value'] : ['data', 'row'].includes(rule.source) ? ['source', 'field'] : ['source'])
      if (!['inherit', 'literal', 'data', 'row', 'clear'].includes(rule.source)) throw new Error('项目模板参数来源无效')
    })
  }
  if (action.type === 'product') {
    if (action.routes.length > 200) throw new Error('项目模板产品规则过多')
    action.routes.forEach(route => keys(route, ['productIds', 'productId', 'pageId']))
    if (action.fallback !== undefined) keys(action.fallback, action.fallback.type === 'page' ? ['type', 'pageId'] : ['type'])
  }
  const safe = copy(action)
  delete safe.pageId
  if (safe.routes) safe.routes.forEach(route => { delete route.pageId })
  if (safe.type === 'pages') safe.items.forEach(item => { delete item.pageId })
  if (safe.fallback) delete safe.fallback.pageId
  pageValueGuard(safe)
}

function remapNavigation (action, ids) {
  if (!action) return
  if (action.type === 'page') action.pageId = ids.get(action.pageId)
  if (action.type === 'pages') action.items.forEach(item => { item.pageId = ids.get(item.pageId) })
  if (action.type === 'product') {
    action.routes.forEach(route => { route.pageId = ids.get(route.pageId) })
    if (action.fallback && action.fallback.type === 'page') action.fallback.pageId = ids.get(action.fallback.pageId)
  }
}

// 编辑面板切换动作/参数来源时会留下旧值，只保存当前动作实际使用的字段。
function templateNavigation (action, pages) {
  if (!object(action)) throw new Error('项目模板页面交互格式不正确')
  const result = { type: action.type }
  if (action.type === 'pages') {
    result.source = action.source; result.inheritContext = action.inheritContext !== false
    if (!Array.isArray(action.items)) throw new Error('导航项必须是列表')
    result.items = action.source === 'all' ? [] : action.items.map(item => {
      if (!object(item)) throw new Error('导航项格式无效')
      return { pageId: item.pageId, ...(item.label !== undefined ? { label: item.label } : {}) }
    })
  }
  if (action.type === 'page') {
    result.pageId = action.pageId
    if (action.params !== undefined) {
      if (!object(action.params) || Object.keys(action.params).some(key => !['productId', 'deviceId'].includes(key))) throw new Error('项目模板参数格式不正确')
      result.params = {}
      Object.entries(action.params).forEach(([key, rule]) => {
        if (!object(rule)) throw new Error('项目模板参数规则无效')
        result.params[key] = { source: rule.source }
        if (rule.source === 'literal') result.params[key].value = rule.value
        if (['data', 'row'].includes(rule.source)) result.params[key].field = rule.field
      })
    }
  }
  if (action.type === 'product') {
    if (!Array.isArray(action.routes)) throw new Error('产品跳转规则必须是列表')
    result.routes = action.routes.map(route => {
      if (!object(route)) throw new Error('产品跳转规则格式无效')
      return { pageId: route.pageId, ...(Object.prototype.hasOwnProperty.call(route, 'productIds') ? { productIds: copy(route.productIds) } : { productId: route.productId }) }
    })
    if (action.fallback !== undefined) {
      if (!object(action.fallback)) throw new Error('默认产品动作无效')
      result.fallback = { type: action.fallback.type }
      if (action.fallback.type === 'page') result.fallback.pageId = action.fallback.pageId
    }
  }
  navigationGuard(result, pages)
  return result
}

function validateProjectSnapshot (snapshot) {
  if (snapshot.schemaVersion !== 1 || Object.keys(snapshot).some(key => !['schemaVersion', 'kind', 'homePageId', 'pages'].includes(key)) ||
      !Array.isArray(snapshot.pages) || !snapshot.pages.length || snapshot.pages.length > 50 || JSON.stringify(snapshot).length > 512000) throw new Error('项目模板须包含1～50个页面，配置不能超过500 KB')
  const ids = new Set()
  let count = 0
  snapshot.pages.forEach(page => {
    if (!object(page) || Object.keys(page).some(key => !['id', 'name', 'config', 'nodes'].includes(key)) || typeof page.id !== 'string' || !/^[A-Za-z0-9_-]{1,64}$/.test(page.id) || ids.has(page.id) ||
        typeof page.name !== 'string' || !page.name.trim() || page.name.length > 60 || !object(page.config)) throw new Error('项目模板页面无效或编号重复')
    ids.add(page.id)
    pageValueGuard(page.name)
  })
  if (!ids.has(snapshot.homePageId)) throw new Error('项目模板首页不存在')
  snapshot.pages.forEach(page => {
    const nodes = copy(page.nodes)
    const walk = (list, depth = 0) => {
      if (!Array.isArray(list) || depth > 12) throw new Error('项目模板组件格式不正确或分组层级过深')
      list.forEach(node => {
        if (!object(node) || ++count > 1000) throw new Error('项目模板总图层不能超过1000')
        if (node.navigation !== undefined) {
          if (node.children) throw new Error('分组不能声明页面交互')
          if (node.component && node.component.name === 'componentTitle') throw new Error('组件标题不支持页面交互')
          if (node.navigation.type === 'product' && (!node.component || !['productList', 'deviceList'].includes(node.component.name))) throw new Error('按产品跳转仅适用于产品列表或设备列表组件')
          if (node.navigation.type === 'pages' && (!node.component || node.component.name !== 'navigation' || node.component.prop !== 'navigation' || !node.option || node.option.mode !== 'pages')) throw new Error('多页面导航仅适用于页面导航组件')
          navigationGuard(node.navigation, snapshot.pages); delete node.navigation
        }
        if (node.children) walk(node.children, depth + 1)
      })
    }
    walk(nodes)
    validateTemplateSnapshot({ schemaVersion: 1, kind: 'page', width: page.config.width, height: page.config.height, config: page.config, nodes })
  })
  return copy(snapshot)
}

// 项目模板保留内部页面交互；页面模板继续清除跨项目跳转。
export function createProjectTemplateSnapshot (project) {
  if (!object(project) || !Array.isArray(project.pages) || !project.pages.length || project.pages.length > 50) throw new Error('项目模板须包含1～50个页面')
  const ids = new Map(project.pages.map((page, i) => [page.id, 'tpl_page_' + (i + 1)]))
  if (ids.size !== project.pages.length || !ids.has(project.homePageId)) throw new Error('项目页面编号重复或首页不存在')
  const pages = project.pages.map(page => {
    const value = createPageTemplateSnapshot(page.config, page.nodes)
    const sources = templateLeaves(page.nodes)
    templateLeaves(value.nodes).forEach((node, i) => {
      if (sources[i].navigation !== undefined && node.component.name !== 'componentTitle') {
        node.navigation = templateNavigation(sources[i].navigation, project.pages)
        remapNavigation(node.navigation, ids)
      }
    })
    return { id: ids.get(page.id), name: page.name, config: value.config, nodes: value.nodes }
  })
  return validateTemplateSnapshot({ schemaVersion: 1, kind: 'project', homePageId: ids.get(project.homePageId), pages })
}

export function templateHome (snapshot) { return snapshot.kind === 'project' ? snapshot.pages.find(page => page.id === snapshot.homePageId) : snapshot }
export function allTemplateLeaves (snapshot) { return snapshot.kind === 'project' ? snapshot.pages.flatMap(page => templateLeaves(page.nodes)) : templateLeaves(snapshot.nodes) }

export function instantiateProjectTemplate (snapshot, createNodeId, createPage = createPageId) {
  const value = validateTemplateSnapshot(snapshot)
  if (value.kind !== 'project') throw new Error('当前模板不是项目模板')
  const ids = new Map(value.pages.map(page => [page.id, createPage()]))
  if (new Set(ids.values()).size !== ids.size || Array.from(ids.values()).some(id => typeof id !== 'string' || !/^[A-Za-z0-9_-]{1,64}$/.test(id))) throw new Error('新页面编号无效或重复')
  const pages = value.pages.map(page => {
    const nodes = cloneNodes(page.nodes, createNodeId, { x: 0, y: 0 }, { preserveLocks: true })
    templateLeaves(nodes).forEach(node => {
      remapNavigation(node.navigation, ids)
      if (['productList', 'deviceList'].includes(node.component.name)) { delete node.option.templateListSource }
    })
    return { ...page, id: ids.get(page.id), config: { ...page.config, name: page.name }, nodes }
  })
  return { version: 1, homePageId: ids.get(value.homePageId), pages }
}

export function configureTemplateDevices (snapshot, preserveFixedDevices = true) {
  const value = validateTemplateSnapshot(snapshot)
  if (!preserveFixedDevices && ['page', 'project'].includes(value.kind)) allTemplateLeaves(value).forEach(node => {
    const option = node.option || {}
    if (node.parameter && node.parameter.deviceSource === 'fixed') { delete node.parameter.deviceId; delete node.parameter.deviceName }
    if (['deviceProperty'].includes(node.component.name) && option.deviceSource === 'fixed') { delete option.deviceId; delete option.deviceName }
    if (node.component.name === 'Player') { delete option.deviceId; delete option.channelId }
  })
  return value
}

export function hasTemplateFixedDevices (snapshot) {
  return ['page', 'project'].includes(snapshot.kind) && allTemplateLeaves(snapshot).some(node =>
    (node.parameter && node.parameter.deviceSource === 'fixed' && node.parameter.deviceId) ||
    (['deviceProperty'].includes(node.component.name) && node.option && node.option.deviceSource === 'fixed' && node.option.deviceId) ||
    (node.component.name === 'Player' && node.option && node.option.deviceId))
}

export function instantiateTemplate (snapshot, createId, point = { x: 0, y: 0 }) {
  const value = validateTemplateSnapshot(snapshot)
  if (value.kind === 'project') throw new Error('项目模板请从资源中心创建新大屏')
  const nodes = cloneNodes(value.nodes, createId, value.kind === 'page' ? { x: 0, y: 0 } : point, { preserveLocks: value.kind === 'page' })
  templateLeaves(nodes).forEach(node => {
    // 列表实例恢复正式平台数据源；模板缩略图保持静态，不触发请求。
    if (['productList', 'deviceList'].includes(node.component.name)) {
      // 旧页面模板只保存空静态占位列表，应用时沿用旧版平台来源行为。
      if (value.kind === 'component' || node.option.templateListSource === undefined) node.option.dataSource = 'platform'
      delete node.option.templateListSource
    }
  })
  return nodes
}

export function beginTemplateDrag (item) {
  endTemplateDrag()
  const snapshot = validateTemplateSnapshot(item.snapshot)
  const key = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2)
  dragging.set(key, { ...item, snapshot })
  return key
}
export function endTemplateDrag () { dragging.clear() }
export function templateFromDrag (key) {
  const item = dragging.get(key)
  endTemplateDrag()
  return item ? copy(item) : null
}
